import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import "./Editor3d.css";
import { createRenderer } from "./components/renderer.js";
import { createCamera } from "./components/camera.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { addStudioLights, addEnvironment, applyPrintedMaterial, placeOnGround, createSubtleGrid, createShadowCatcher, frameCameraOnObject } from "./components/studio.js";
import * as dat from 'lil-gui'
import {
    applyLayoutTargets,
    computeLayoutTargets,
    createLayoutTween,
    measureAssembly,
    stepLayoutTween as stepTween,
    addCoasterStack,
} from './partsLayout.js';
import {
    createSectionPlane,
    disableSection,
    enableSection,
    moveCaps,
    resizeCaps,
    sectionHeightUnits,
    setSectionPlane,
    syncCapColors,
} from './sectionView.js';
const DEBUG = false;

const disposeSceneResources = (scene) => {
    scene.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
            if (Array.isArray(child.material)) child.material.forEach((material) => material.dispose());
            else child.material.dispose();
        }
    });
};

// Posiciones para mirar la pieza (C3D-8), como giro del pivote que la envuelve.
// El GLB llega tumbado con la cara del dibujo hacia +Y y la parte de arriba del
// diseño hacia -Z (ver `_export` en el runner del backend):
// - `flat`: tal cual, boca arriba.
// - `flipped`: boca abajo, girada sobre el eje arriba-abajo del diseño (Z),
//   como se le da la vuelta a una tarjeta: se ve la cara de atrás sin invertir.
// - `upright`: de pie mirando a la cámara, el mismo giro que el AR de pared
//   (`_wall_rotated`: +90° sobre X).
export const VIEW_ORIENTATIONS = {
    flat: [0, 0, 0],
    flipped: [0, 0, Math.PI],
    upright: [Math.PI / 2, 0, 0],
};

// `drawing_ff0000_d150_b__objk3x9a1`: color de origen tras `drawing_` y, si
// el editor lo marcó, el objeto del que sale (ver `_partUid` en editor.js).
const getSvgPartInfo = (name, fallbackColor) => {
    const normalizedName = String(name || '');
    const hexMatch = normalizedName.match(/#?([0-9a-fA-F]{6})(?:\.\d+)?$/)
        || normalizedName.match(/drawing_(?:\d{4}_)?([0-9a-fA-F]{6})/);
    const objectMatch = normalizedName.match(/__obj([a-z0-9]{6})/i);
    return {
        sourceColor: hexMatch ? `#${hexMatch[1].toUpperCase()}` : fallbackColor,
        isBackground: normalizedName.toLowerCase().includes('background'),
        objectUid: objectMatch ? objectMatch[1].toLowerCase() : null,
    };
};

const ThreeJSComponent = ({ modelId, revision = 0, className, onLoaded, onError, onPartsLoaded, partsVisibility, partsColors, partsLayout = 'assembled', separationMm = 45, sectionEnabled = false, sectionHeightPct = 50, sectionKeep = 'bottom', gridVisible = true, orientation = 'flat', cameraPoseRef }) => {

    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    if (!sceneRef.current) sceneRef.current = new THREE.Scene();
    if (!cameraRef.current) cameraRef.current = createCamera();
    if (!rendererRef.current) rendererRef.current = createRenderer();
    const controlsRef = useRef(null)
    // El modelo cuelga de este pivote: girarlo cambia cómo se mira la pieza sin
    // tocar las medidas ni los modos de piezas, que trabajan en local.
    const pivotRef = useRef(null)
    if (!pivotRef.current) pivotRef.current = new THREE.Group()
    const orientationRef = useRef(orientation)
    const onLoadedRef = useRef(onLoaded)
    const onErrorRef = useRef(onError)
    const onPartsLoadedRef = useRef(onPartsLoaded)
    const gridHelperRef = useRef(null)
    const gridVisibleRef = useRef(gridVisible)
    // Mallas del modelo cargado, para poder togglear su visibilidad sin recargar.
    const partsRef = useRef([])
    // Dimensiones del modelo ensamblado y cuánto hay que bajar la pieza frontal
    // para que, al separarla, quede apoyada en el mismo plano que la trasera.
    const modelSizeRef = useRef(new THREE.Vector3())
    const frontDropRef = useRef(0)

    // El modo de vista se guarda también en refs para que el callback de carga
    // del GLB pueda reaplicarlo: al regenerar, las mallas son nuevas, y sin esto
    // la vista volvería a "montada" cada vez — justo mientras se itera sobre la
    // geometría, que es cuando más molesta.
    const layoutRef = useRef(partsLayout)
    const separationRef = useRef(separationMm)
    // Transición en curso entre dos disposiciones: la tapa no salta de golpe,
    // sube. Ver `stepLayoutTween`, que la avanza en el bucle de render.
    const layoutTweenRef = useRef(null)

    // Seccionado: el plano vive mientras viva el componente (los materiales
    // guardan una referencia a él, así que moverlo basta para recolocar el
    // corte). El cap solo existe mientras el corte está activo.
    const sectionPlaneRef = useRef(null)
    if (!sectionPlaneRef.current) sectionPlaneRef.current = createSectionPlane()
    const sectionCapsRef = useRef(null)
    // Igual que el modo de vista: en refs para que el callback de carga del GLB
    // pueda reaplicar el corte sobre las mallas nuevas.
    const sectionRef = useRef({ enabled: sectionEnabled, heightPct: sectionHeightPct, keep: sectionKeep })

    /**
     * Llevar las piezas a una disposición, animando el recorrido.
     *
     * `animated: false` lo aplica en seco: al cargar un GLB nuevo las mallas
     * acaban de nacer y no hay un "desde" del que partir, así que animarlas
     * sería un salto desde el origen.
     *
     * Declarado ANTES del efecto de carga porque ese efecto lo referencia en su
     * array de dependencias, que se evalúa en el momento de la llamada.
     */
    const applyPartsLayout = useCallback((layout, separation, { animated = true } = {}) => {
        const targets = computeLayoutTargets(partsRef.current, layout, separation, {
            modelWidthUnits: modelSizeRef.current.x || 0,
            frontDrop: frontDropRef.current,
        });
        if (!targets.length) return;

        if (!animated) {
            layoutTweenRef.current = null;
            applyLayoutTargets(targets);
            return;
        }
        layoutTweenRef.current = createLayoutTween(targets);
    }, []);

    /** Avanza la transición un frame. Devuelve true si sigue viva. */
    const stepLayoutTween = useCallback(() => {
        if (stepTween(layoutTweenRef.current)) return true;
        layoutTweenRef.current = null;
        return false;
    }, []);

    /**
     * Pone el corte al día: lo monta, lo mueve o lo desmonta.
     *
     * Se llama tanto desde el efecto de las props como desde el callback de
     * carga del GLB. Ahí importa: al regenerar el modelo las mallas son nuevas
     * y no llevan ni el plano en sus materiales ni los meshes de stencil, así
     * que sin esto el corte desaparecería en cada regeneración.
     *
     * Declarado ANTES del efecto de carga porque ese efecto lo referencia.
     */
    const applySectionView = useCallback(({ enabled, heightPct, keep }) => {
        const scene = sceneRef.current;
        const plane = sectionPlaneRef.current;
        const meshes = partsRef.current.map((part) => part.mesh).filter(Boolean);

        if (!enabled) {
            if (sectionCapsRef.current) {
                disableSection(scene, meshes, sectionCapsRef.current);
                sectionCapsRef.current = null;
            }
            return;
        }

        // Girada, la altura que se corta ya no es la de la pieza tumbada.
        const modelSize = orientationRef.current === 'flat'
            ? modelSizeRef.current
            : new THREE.Box3().setFromObject(pivotRef.current).getSize(new THREE.Vector3());
        const heightUnits = sectionHeightUnits(modelSize.y, heightPct);
        setSectionPlane(plane, { heightUnits, keep });

        if (!sectionCapsRef.current) {
            sectionCapsRef.current = enableSection(scene, meshes, plane, { modelSize });
        } else {
            resizeCaps(sectionCapsRef.current, modelSize);
        }
        moveCaps(sectionCapsRef.current, heightUnits);
    }, []);

    /**
     * Gira la pieza a una de las `VIEW_ORIENTATIONS`, la vuelve a apoyar en la
     * malla y reencuadra la cámara.
     *
     * Declarado ANTES del efecto de carga porque ese efecto lo referencia.
     */
    const applyOrientation = useCallback((name, { frame = true } = {}) => {
        const pivot = pivotRef.current;
        if (!pivot.children.length) return;
        const [x, y, z] = VIEW_ORIENTATIONS[name] ?? VIEW_ORIENTATIONS.flat;
        pivot.rotation.set(x, y, z);
        pivot.position.set(0, 0, 0);
        pivot.updateMatrixWorld(true);
        placeOnGround(pivot);
        pivot.updateMatrixWorld(true);
        // El corte depende de la altura de la pieza, que acaba de cambiar.
        applySectionView(sectionRef.current);
        if (frame && cameraRef.current && controlsRef.current) {
            frameCameraOnObject(cameraRef.current, controlsRef.current, pivot, { duration: 0.8 });
        }
    }, [applySectionView]);

    useEffect(() => {
        onLoadedRef.current = onLoaded
        onErrorRef.current = onError
        onPartsLoadedRef.current = onPartsLoaded
    }, [onLoaded, onError, onPartsLoaded])



    // useEffect(() => {
    //     axios.get(`http://localhost:8005/customized-keyrings/${modelId}/params/`)
    //         .then((response) => {
    //             console.log('params', response.data);
    //         })
    //         .catch((error) => {
    //             console.error(error);
    //         });
    // }, []);

    useEffect(() => {
        const container = containerRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const renderer = rendererRef.current;
        let animationFrameId = null;
        let retryTimeoutId = null;
        let disposed = false;
        let modelLoaded = false;

        // La escena se monta aunque todavía no haya archivo que cargar.
        //
        // `revision === 0` significa "el GLB se está generando": entrar en 3D
        // debe enseñar ya el grid, las luces y la cámara, o durante toda la
        // generación se ve un vacío liso. El modelo entra después, cuando la
        // revisión sube y este efecto vuelve a correr.
        const hasModelToLoad = Boolean(modelId) && Number(revision) > 0;

        // Sin id cuando SÍ tocaba cargar es un fallo de verdad, y el llamador
        // tiene que enterarse.
        if (!modelId && Number(revision) > 0) {
            onErrorRef.current?.()
            return undefined
        }

        renderer.setSize(window.innerWidth, window.innerHeight);
        container.appendChild(renderer.domElement);

        // Misma iluminación que Mi3DViewer/Public3dViewer: la luz inferior
        // insinúa la cara de abajo de la pieza al orbitar por debajo del grid.
        addStudioLights(scene, { bottomLight: true });
        const environmentTexture = addEnvironment(scene, renderer);

        const gridHelper = createSubtleGrid(100, 100);
        gridHelper.visible = gridVisibleRef.current;
        gridHelperRef.current = gridHelper;
        scene.add(gridHelper);

        // Plano receptor de la sombra de contacto (invisible salvo la sombra).
        // Se mantiene aunque se oculte el grid: es lo que "asienta" la pieza.
        const shadowCatcher = createShadowCatcher(400);
        scene.add(shadowCatcher);

        // const axesHelper = new THREE.AxesHelper(100);
        // scene.add(axesHelper);

        const loadingManager = new THREE.LoadingManager();
        const dracoLoader = new DRACOLoader().setDecoderPath('/node_modules/three/examples/jsm/libs/draco/gltf/');
        const gltfLoader = new GLTFLoader(loadingManager)
            .setDRACOLoader(dracoLoader)
            .setRequestHeader({ 'Authorization': 'Bearer ' + localStorage.getItem('access_token') });



        if (DEBUG) {
            const gui = new dat.GUI();
            const cameraFolder = gui.addFolder('Camera');
            //add axis
            cameraFolder.add(camera.position, 'x', -100, 100).listen();
            cameraFolder.add(camera.position, 'y', -100, 100).listen();
            cameraFolder.add(camera.position, 'z', -100, 100).listen();
            cameraFolder.open();
        }


        const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;


        const loadModel = (attempt = 0) => gltfLoader.load(
                `${API_BASE_URL}customized-keyrings/${modelId}/glb/?revision=${revision}&attempt=${attempt}`,
            (gltf) => {
                if (disposed || modelLoaded) return
                modelLoaded = true

                if (retryTimeoutId !== null) {
                    window.clearTimeout(retryTimeoutId)
                    retryTimeoutId = null
                }

                try {

                const object = gltf.scene;
                // No extra scale here: the backend GLB is already calibrated
                // to the viewer's grid (1 Three.js unit = 1cm). This used to
                // be `object.scale.set(35, 35, 35)`, a leftover factor tuned
                // for the legacy Blender pipeline's absolute GLB scale, which
                // silently compounded with any backend scale change.

                // Se mide y se coloca con el pivote sin girar; la posición de
                // vista se aplica al final.
                const pivot = pivotRef.current;
                pivot.rotation.set(0, 0, 0);
                pivot.position.set(0, 0, 0);
                pivot.updateMatrixWorld(true);
                pivot.add(object);
                scene.add(pivot);

                applyPrintedMaterial(object);

                // Recolecta las piezas (mallas) del modelo para el panel de
                // visibilidad. El color sale del material ya aplicado.
                const collectedParts = [];
                const nameOccurrences = {};
                object.traverse((child) => {
                    if (child.isMesh) {
                        // El relieve proyecta sombra sobre la base (que la
                        // recibe): así el dibujo se separa del fondo aunque
                        // compartan color.
                        child.castShadow = true;
                        child.receiveShadow = true;
                        const name = child.name || `pieza_${collectedParts.length + 1}`;
                        const occurrence = nameOccurrences[name] || 0;
                        nameOccurrences[name] = occurrence + 1;
                        const key = `${name}::${occurrence}`;
                        const colorHex = child.material?.color
                            ? `#${child.material.color.getHexString()}`
                            : null;
                        const svgPartInfo = getSvgPartInfo(name, colorHex);
                        collectedParts.push({
                            id: child.uuid,
                            key,
                            name,
                            colorHex,
                            ...svgPartInfo,
                            mesh: child,
                            // Anchor for the layout modes, so switching between
                            // them is reversible however many times it happens.
                            basePosition: child.position.clone(),
                        });
                    }
                });
                partsRef.current = collectedParts;
                // La pila de posavasos, si la hay. Después de recolectar (no
                // son piezas propias) y antes de apoyar y medir el modelo.
                addCoasterStack(object, collectedParts);
                // Se entrega una versión serializable (sin la malla Three.js).
                onPartsLoadedRef.current?.(
                    collectedParts.map((part) => ({
                        id: part.id,
                        key: part.key,
                        name: part.name,
                        colorHex: part.colorHex,
                        sourceColor: part.sourceColor,
                        isBackground: part.isBackground,
                        objectUid: part.objectUid,
                    }))
                );

                // Centrar el modelo y apoyar su base sobre la malla (y = 0)
                placeOnGround(object);

                // Medidas para las vistas de piezas separadas. Se toman una sola
                // vez, con el modelo ensamblado, para que separar y volver a
                // juntar no acumule desplazamientos.
                const assembly = measureAssembly(object, collectedParts);
                modelSizeRef.current.copy(assembly.size);
                frontDropRef.current = assembly.frontDrop;

                // Mallas nuevas: hay que volver a colocarlas según el modo activo,
                // y sin animar — no hay posición anterior desde la que salir.
                applyPartsLayout(layoutRef.current, separationRef.current, { animated: false });

                // Mallas nuevas también significa corte nuevo: el plano y el
                // stencil viven en los materiales, que se acaban de recrear.
                applySectionView(sectionRef.current);

                // Encuadra la cámara sobre el modelo (ver studio.js:
                // frameCameraOnObject para el porqué del zoom-out y la
                // elevación proporcional).
                applyOrientation(orientationRef.current, { frame: false });
                // Con arranque y frenada suaves: tras una recarga la cámara
                // sale de donde la dejó el usuario (ver `cameraPoseRef`), y
                // empezar a toda velocidad se lee como un salto.
                frameCameraOnObject(camera, controls, pivot, { duration: 1.5, ease: 'power2.inOut' });

                renderer.render(scene, camera);
                } catch (setupError) {
                    // El modelo ya está en escena; un fallo en el encuadre o los
                    // materiales no debe dejar la UI en estado de carga.
                    console.error('Error preparing loaded model:', setupError);
                }
                if (!disposed) onLoadedRef.current?.()

            },
            (xhr) => {
                console.log((xhr.loaded / xhr.total) * 100 + '% loaded');
            },
            (error) => {
                console.error(error);
                if (disposed || modelLoaded) return

                if (attempt < 2) {
                    retryTimeoutId = window.setTimeout(
                        () => loadModel(attempt + 1),
                        400 * (attempt + 1),
                    )
                    return
                }

                onErrorRef.current?.(error)
            }
        );

        if (hasModelToLoad) loadModel()

        const controls = new OrbitControls(camera, renderer.domElement);
        controlsRef.current = controls;
        // Cada recarga monta un visor nuevo (el padre cambia la `key`), con su
        // cámara en la posición de partida. Sin recuperar dónde estaba la
        // anterior, el encuadre saltaba a esa posición fija antes de animar
        // (C3D-29).
        const savedPose = cameraPoseRef?.current;
        if (savedPose) {
            camera.position.copy(savedPose.position);
            controls.target.copy(savedPose.target);
            controls.update();
        }

        const animate = () => {
            stepLayoutTween();
            renderer.render(scene, camera);
            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        const handleResize = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;

            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();

            renderer.render(scene, camera);
        };

        window.addEventListener('resize', handleResize);
        const handleContextLost = (event) => {
            event.preventDefault();
            console.warn('Contexto WebGL perdido en el visor 3D');
        };
        renderer.domElement.addEventListener('webglcontextlost', handleContextLost, false);

        return () => {
            disposed = true
            if (cameraPoseRef) {
                cameraPoseRef.current = {
                    position: camera.position.clone(),
                    target: controls.target.clone(),
                };
            }
            window.removeEventListener('resize', handleResize);
            renderer.domElement.removeEventListener('webglcontextlost', handleContextLost);
            if (animationFrameId !== null) cancelAnimationFrame(animationFrameId)
            if (retryTimeoutId !== null) window.clearTimeout(retryTimeoutId)
            controls.dispose()
            dracoLoader.dispose()
            // Antes de vaciar la escena: los meshes de stencil comparten
            // geometría con la pieza que copian, así que se quitan por su
            // camino en vez de dejar que `disposeSceneResources` la libere dos
            // veces. El cap queda a null para que, al recargar el modelo, el
            // corte se vuelva a montar sobre las mallas nuevas.
            if (sectionCapsRef.current) {
                disableSection(scene, partsRef.current.map((part) => part.mesh).filter(Boolean), sectionCapsRef.current)
                sectionCapsRef.current = null
            }
            disposeSceneResources(scene)
            // El pivote sobrevive a la escena: se vacía para el próximo modelo,
            // después de liberar sus mallas (que cuelgan de él).
            pivotRef.current.clear()
            environmentTexture.dispose()
            scene.clear()
            gridHelperRef.current = null
            partsRef.current = []
            if (container.contains(renderer.domElement)) {
                container.removeChild(renderer.domElement);
            }
        };



        // Los tres son useCallback de identidad estable, así que no provocan
        // recargas de la escena por estar aquí. `cameraPoseRef` tampoco: es
        // un ref, siempre el mismo objeto.
    }, [modelId, revision, applyPartsLayout, stepLayoutTween, applySectionView, applyOrientation, cameraPoseRef]);

    useEffect(() => {
        if (orientationRef.current === orientation) return;
        orientationRef.current = orientation;
        applyOrientation(orientation);
    }, [orientation, applyOrientation]);

    useEffect(() => {
        gridVisibleRef.current = gridVisible;
        if (gridHelperRef.current) {
            gridHelperRef.current.visible = gridVisible;
        }
    }, [gridVisible]);

    // Aplica la visibilidad de cada pieza sin recargar el modelo. El bucle de
    // render (requestAnimationFrame) refleja el cambio en el siguiente frame.
    useEffect(() => {
        if (!partsVisibility) return;
        partsRef.current.forEach((part) => {
            if (part.mesh && Object.prototype.hasOwnProperty.call(partsVisibility, part.id)) {
                part.mesh.visible = partsVisibility[part.id] !== false;
            }
        });
    }, [partsVisibility]);

    // El modo de vista se sincroniza a refs para que el callback de carga del
    // GLB pueda reaplicarlo (ver `applyPartsLayout`, declarado arriba).
    //
    // Solo se anima el cambio de MODO. Arrastrar el slider de separación emite
    // un cambio por frame, y lanzar una transición de 700 ms en cada uno haría
    // que la pieza fuese siempre por detrás del dedo.
    useEffect(() => {
        const layoutChanged = layoutRef.current !== partsLayout;
        layoutRef.current = partsLayout;
        separationRef.current = separationMm;
        applyPartsLayout(partsLayout, separationMm, { animated: layoutChanged });
    }, [partsLayout, separationMm, applyPartsLayout]);

    // El corte se recalcula en cuanto cambia cualquiera de sus tres controles.
    // Mover el slider solo desplaza el plano y el cap: el stencil ya está
    // montado, así que arrastrarlo no reconstruye nada.
    useEffect(() => {
        sectionRef.current = { enabled: sectionEnabled, heightPct: sectionHeightPct, keep: sectionKeep };
        applySectionView(sectionRef.current);
    }, [sectionEnabled, sectionHeightPct, sectionKeep, applySectionView]);

    // Los colores se aplican sobre las mallas ya cargadas para que el cambio se
    // previsualice al instante, sin regenerar ni volver a descargar el GLB.
    useEffect(() => {
        if (!partsColors) return;
        partsRef.current.forEach((part) => {
            const color = partsColors[part.key];
            if (color && part.mesh?.material?.color) {
                part.mesh.material.color.set(color);
                part.mesh.material.needsUpdate = true;
            }
        });
        // La superficie de corte de cada pieza va de su color, así que hay que
        // releerlo: si no, repintar una pieza dejaría su sección del anterior.
        syncCapColors(sectionCapsRef.current);
    }, [partsColors]);


    // `w-full h-full`: sin tamaño propio el div medía 0 y su color de fondo no
    // pintaba nada, así que lo que se veía detrás del canvas (transparente
    // mientras no hay modelo) era el fondo de la página.
    return <div className={`w-full h-full ${className || ''}`} ref={containerRef} />;
};

ThreeJSComponent.propTypes = {
    modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    revision: PropTypes.number,
    className: PropTypes.string,
    onLoaded: PropTypes.func,
    onError: PropTypes.func,
    onPartsLoaded: PropTypes.func,
    partsVisibility: PropTypes.object,
    partsColors: PropTypes.object,
    partsLayout: PropTypes.oneOf(['assembled', 'open', 'separated']),
    separationMm: PropTypes.number,
    sectionEnabled: PropTypes.bool,
    sectionHeightPct: PropTypes.number,
    sectionKeep: PropTypes.oneOf(['bottom', 'top']),
    gridVisible: PropTypes.bool,
    orientation: PropTypes.oneOf(Object.keys(VIEW_ORIENTATIONS)),
    // Ref del padre donde el visor deja la cámara al desmontarse y de donde la
    // recoge al montarse: así sobrevive a las recargas por `key`.
    cameraPoseRef: PropTypes.shape({ current: PropTypes.object }),
}

export default ThreeJSComponent;
